import type { FieldIssue, PolicyDecision, ProblemCode, ProblemDetails } from './types/common';
import type { DuplicateCandidate } from './types/patients';
import type { SafetyFinding } from './types/orders';

/** Every non-2xx API response becomes an ApiError carrying the parsed RFC 9457 problem. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ProblemCode | (string & {});
  readonly problem: ProblemDetails;
  readonly requestId?: string;

  constructor(problem: ProblemDetails, requestId?: string) {
    super(problem.detail || problem.title);
    this.name = 'ApiError';
    this.status = problem.status;
    this.code = problem.code;
    this.problem = problem;
    this.requestId = problem.requestId || requestId;
  }

  is(code: ProblemCode): boolean {
    return this.code === code;
  }

  get title(): string {
    return this.problem.title;
  }
  get detail(): string | undefined {
    return this.problem.detail;
  }
  get fieldErrors(): FieldIssue[] {
    return this.problem.errors ?? [];
  }
  get policy(): PolicyDecision | undefined {
    return normalizePolicy(this.problem.policy);
  }
  get candidates(): DuplicateCandidate[] {
    return this.problem.candidates ?? [];
  }
  get findings(): SafetyFinding[] {
    return this.problem.findings ?? [];
  }
  /** Safe reference code for the person to quote to support. */
  get reference(): string {
    return this.requestId ?? this.problem.traceId ?? 'unknown';
  }
}

/** The network itself failed (offline, DNS, proxy down). Distinct from a server problem. */
export class NetworkError extends Error {
  constructor(message = "The ETNAS service could not be reached.", override readonly cause?: unknown) {
    super(message);
    this.name = 'NetworkError';
  }
}

export function isApiError(e: unknown, code?: ProblemCode): e is ApiError {
  return e instanceof ApiError && (code ? e.code === code : true);
}

export function isNetworkError(e: unknown): e is NetworkError {
  return e instanceof NetworkError;
}

/* ---------------- Policy decisions (R-107) ---------------- */

/** core-api reason codes (platform/policy.ts) → words for PolicyExplain. */
export const POLICY_REASONS: Record<string, { label: string; value: string }> = {
  ACCESS_DENIED: { label: 'Access policy', value: 'Not permitted for your role, purpose or relationship to this record' },
  ACCESS_ALLOWED: { label: 'Access policy', value: 'Permitted for your role and context' },
  PERMISSION_AND_CONTEXT_VALID: { label: 'Permission and context', value: 'Granted for your role and context' },
  TENANT_MISMATCH: { label: 'Organization', value: 'The record belongs to another organization' },
  PERMISSION_MISSING: { label: 'Role permission', value: 'Your active role does not include this action' },
  SERVICE_DELEGATION_REQUIRED: { label: 'Service account', value: 'Needs an explicit user delegation' },
  CONTROL_PLANE_NO_PHI: { label: 'Platform operator', value: 'Platform operators never see patient data' },
  LOCATION_OUT_OF_SCOPE: { label: 'Location', value: 'Outside the location of your membership' },
  DEVICE_RISK: { label: 'Device', value: 'This device is not trusted for this action' },
  CREDENTIAL_EXPIRED: { label: 'Credential', value: 'Your professional credential is not current' },
  PURPOSE_NOT_PERMITTED: { label: 'Purpose of use', value: 'Your role cannot use this purpose for patient data' },
  CARE_RELATIONSHIP_REQUIRED: { label: 'Care relationship', value: 'You are not on this patient’s care team' },
  PROXY_SCOPE_REQUIRED: { label: 'Proxy scope', value: 'Not shared with you by the patient' },
  CONSENT_DENIED: { label: 'Consent', value: 'The patient has refused this use' },
  LEGAL_PROHIBITION: { label: 'Legal restriction', value: 'Law prohibits access without specific consent' },
  SENSITIVITY_RESTRICTED: { label: 'Sensitivity', value: 'Restricted category; consent or emergency access needed' },
  PROXY_CATEGORY_RESTRICTED: { label: 'Proxy category', value: 'This category is private to the patient' },
  SEGREGATION_OF_DUTIES: { label: 'Separation of duties', value: 'A different person must do this' },
};

const OBLIGATIONS: Record<string, string> = {
  'audit.access': 'Access is recorded in the audit log',
  'support.capture': 'Support session recorded',
  'compliance.review': 'Reviewed by compliance',
  'break_glass.capture': 'Every view recorded under emergency access',
};

/**
 * Accept the UI PolicyDecision shape or the core-api shape
 * `{decision:'permit'|'deny', reasons[], obligations[], policyVersion, correlationId}`.
 */
export function normalizePolicy(raw: unknown): PolicyDecision | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  if (Array.isArray(r.inputs)) return r as unknown as PolicyDecision;
  const decision = r.decision === 'permit' || r.decision === 'allow' ? 'allow' : r.decision === 'conditional' ? 'conditional' : 'deny';
  const reasons = Array.isArray(r.reasons) ? (r.reasons as string[]) : [];
  return {
    decision,
    action: typeof r.action === 'string' ? r.action : typeof r.permission === 'string' ? r.permission : 'This action',
    inputs: reasons.map((code) => ({
      label: POLICY_REASONS[code]?.label ?? code.replace(/_/g, ' ').toLowerCase(),
      value: POLICY_REASONS[code]?.value ?? code,
      result: decision === 'allow' ? ('pass' as const) : ('fail' as const),
    })),
    obligations: Array.isArray(r.obligations) ? (r.obligations as string[]).map((o) => OBLIGATIONS[o] ?? o) : [],
    policyVersion: typeof r.policyVersion === 'string' ? r.policyVersion : 'policy',
    reason: reasons.length && decision !== 'allow' ? reasons.map((c) => POLICY_REASONS[c]?.value ?? c).join('; ') : undefined,
    correlationId: typeof r.correlationId === 'string' ? r.correlationId : undefined,
  };
}

/* ---------------- Version conflicts (R-032) ---------------- */

/**
 * The server's current copy for a 412 ETNAS_VERSION_CONFLICT. Uses `problem.current` / `currentEtag` when the
 * backend sends them; otherwise re-reads the aggregate (never retries with the stale tag).
 */
export async function conflictCurrent<T>(e: ApiError, refetch: () => Promise<{ data: T; etag: string | null }>): Promise<{ data: T; etag: string | null; changedBy?: string; changedAt?: string }> {
  const p = e.problem;
  if (p.current !== undefined && p.current !== null) {
    const cur = p.current as T & { rowVersion?: string | number };
    const etag = p.currentEtag ?? (cur.rowVersion != null ? `W/"${cur.rowVersion}"` : null);
    if (etag) return { data: cur, etag, changedBy: p.changedBy, changedAt: p.changedAt };
  }
  const fresh = await refetch();
  return { ...fresh, changedBy: p.changedBy, changedAt: p.changedAt };
}

const STATUS_CODE: Record<number, ProblemCode> = {
  401: 'ETNAS_UNAUTHENTICATED',
  403: 'ETNAS_FORBIDDEN',
  404: 'ETNAS_NOT_FOUND',
  409: 'ETNAS_ILLEGAL_TRANSITION',
  412: 'ETNAS_VERSION_CONFLICT',
  422: 'ETNAS_VALIDATION',
  428: 'ETNAS_PRECONDITION_REQUIRED',
  429: 'ETNAS_RATE_LIMITED',
};

/**
 * Parse any error response into a ProblemDetails. Tolerates non-problem bodies (proxies, HTML error pages)
 * by synthesising a problem from the status so callers can always switch on `code`.
 */
export async function parseProblem(res: Response): Promise<ProblemDetails> {
  const ct = res.headers.get('content-type') || '';
  let body: unknown = null;
  try {
    if (ct.includes('json')) body = await res.json();
    else {
      const text = await res.text();
      body = text ? { detail: text.slice(0, 300) } : null;
    }
  } catch {
    body = null;
  }
  const b = (body && typeof body === 'object' ? body : {}) as Partial<ProblemDetails> & Record<string, unknown>;
  const status = typeof b.status === 'number' ? b.status : res.status;
  const code = (typeof b.code === 'string' && b.code) || STATUS_CODE[status] || (status >= 500 ? 'ETNAS_INTERNAL' : 'ETNAS_VALIDATION');
  return {
    ...b,
    type: typeof b.type === 'string' ? b.type : 'about:blank',
    title: typeof b.title === 'string' ? b.title : defaultTitle(status),
    status,
    code,
    requestId: (typeof b.requestId === 'string' && b.requestId) || res.headers.get('x-request-id') || undefined,
  } as ProblemDetails;
}

function defaultTitle(status: number): string {
  if (status >= 500) return 'The service had a problem';
  if (status === 401) return 'Sign in again to continue';
  if (status === 403) return 'You do not have access to this';
  if (status === 404) return 'Not found';
  if (status === 412) return 'The record changed before this action completed';
  if (status === 429) return 'Too many requests';
  return 'The request could not be completed';
}

/** Human message for toasts and inline errors. Never exposes internals beyond the reference code. */
export function describeError(e: unknown): { title: string; body: string; reference?: string } {
  if (e instanceof ApiError) {
    return { title: e.title, body: e.detail ?? '', reference: e.reference };
  }
  if (e instanceof NetworkError) {
    return { title: 'You appear to be offline', body: 'Your drafts are kept. Signing, ordering and release wait until the connection returns.' };
  }
  return { title: 'Something went wrong', body: e instanceof Error ? e.message : String(e) };
}
